import React, { FunctionComponent, SyntheticEvent, useContext } from "react";
import { Alert, ChoiceList } from "@cmsgov/ds-medicare-gov";
import { FocusableHeader, HelpDrawerWrapper } from "../../../components";
import { EnrollmentFormProps2023 } from "./../EnrollmentFormPage";
import BackNextButtons from "./../BackNextButtons";
import { useTranslate } from "../../../helpers";
import PlanInfo from "./../PlanInfo";
import { FormattedMessage, FormattedHTMLMessage } from "react-intl-phraseapp";
import { GetBenefitsFrom, UserLanguage } from "../../../@types";
import { AppContext } from "../../../app/store";
import { linkToPartDLateEnrollmentPenalty } from "../../../helpers/CONSTANTS";

enum HowPay {
  DIRECT_PAY = "DirectPay",
  DEDUCT = "Deducted",
}

const WhatsExtraHelpDrawer: FunctionComponent = () => {
  const t = useTranslate();
  const {
    state: { language },
  } = useContext(AppContext);

  return (
    <HelpDrawerWrapper
      inlineToggle
      toggleText={t("enroll_form.whats_extra_help")}
      drawerTitle={t("enroll_form.whats_extra_help")}
    >
      <p>{t("enroll_form.whats_extra_help.limited_income")}</p>
      <ul>
        <li>{t("enroll_form.whats_extra_help.limited_income.get_help")}</li>
        <li>
          {t("enroll_form.whats_extra_help.limited_income.no_coverage_gap")}
        </li>
        <li>
          {t("enroll_form.whats_extra_help.limited_income.no_late_penalty")}
        </li>
      </ul>
      <p>
        <a
          href={
            language === UserLanguage.SPANISH
              ? "https://secure.ssa.gov/i1020/startEs"
              : "https://secure.ssa.gov/i1020/start"
          }
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("enroll.paying.2021.qualify_and_apply")}
        </a>{" "}
        {t("enroll.paying.2021.contact_local_sso")}
      </p>
    </HelpDrawerWrapper>
  );
};

const MoreAboutPaymentsDrawer: FunctionComponent = () => {
  const t = useTranslate();
  return (
    <HelpDrawerWrapper
      inlineToggle
      toggleText={t("enroll_form.learn_more_payment_options")}
      drawerTitle={t("enroll_form.learn_more_payment_options")}
    >
      <p>{t("enroll_form.learn_more_payment_options.two_ways")}</p>
      <ol>
        <li>
          <p>
            {t("enroll_form.learn_more_payment_options.automatic_deduction")}
          </p>
          <p>
            {t(
              "enroll_form.learn_more_payment_options.automatic_deduction.delay_warning"
            )}
          </p>
        </li>
        <li>{t("enroll_form.learn_more_payment_options.pay_directly")}</li>
      </ol>
      <p>{t("enroll_form.learn_more_payment_options.change_how_you_pay")}</p>
      <p>
        <strong>{t("remember")}:</strong>
        {` `}
        {t("enroll_form.learn_more_payment_options.remember")}
      </p>
    </HelpDrawerWrapper>
  );
};

const MoreAboutPremiumsOtherHelpDrawer: FunctionComponent = () => {
  const t = useTranslate();

  return (
    <HelpDrawerWrapper
      inlineToggle
      toggleText={`${t("enroll_form.premiums.other_help.learn_more")}.`}
      drawerTitle={t("enroll_form.premiums.other_help.learn_more")}
    >
      <p>{t("enroll_form.premiums.other_help_drawer.info")}</p>
      <ul>
        <li>
          <FormattedHTMLMessage
            id="enroll_form.premiums.other_help_drawer.info.entire_premium"
            tagName="span"
          />
        </li>
        <li>
          <FormattedHTMLMessage
            id="enroll_form.premiums.other_help_drawer.info.pay_directly"
            tagName="span"
          />
        </li>
      </ul>
      <p>
        {t(
          "enroll_form.premiums.other_help_drawer.info.contact_for_more_details"
        )}
      </p>
    </HelpDrawerWrapper>
  );
};

const MoreAboutIrmaaDrawer: FunctionComponent = () => {
  const t = useTranslate();
  const {
    state: { language },
  } = useContext(AppContext);

  return (
    <HelpDrawerWrapper
      inlineToggle
      toggleText={`${t("enroll_form.premiums.partd_irmaa")}`}
      drawerTitle={t("enroll_form.premiums.partd_irmaa.title")}
    >
      <p>{t("enroll_form.premiums.partd_irmaa.notified_by_social_security")}</p>
      <p>{t("enroll_form.premiums.partd_irmaa.usually_deducted")}</p>
      <Alert hideIcon variation="warn">
        {t("enroll_form.premiums.partd_irmaa.do_not_pay")}
      </Alert>
      <p>{t("enroll_form.premiums.partd_irmaa.if_you_dont_pay")}</p>
      <ul>
        <li>
          {t(
            "enroll_form.premiums.partd_irmaa.if_you_dont_pay.loose_drug_coverage"
          )}
        </li>
        <li>
          {t(
            "enroll_form.premiums.partd_irmaa.if_you_dont_pay.loose_health_plan"
          )}
        </li>
        <li>
          <FormattedMessage
            id="enroll_form.premiums.partd_irmaa.if_you_dont_pay.wont_get_special_enrollment"
            tagName="span"
            values={{
              link: (
                <a
                  href={linkToPartDLateEnrollmentPenalty(language)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t(
                    "enroll_form.premiums.partd_irmaa.if_you_dont_pay.learn_more_penalty_link"
                  )}
                </a>
              ),
            }}
          />
        </li>
      </ul>
    </HelpDrawerWrapper>
  );
};

const Paying: FunctionComponent<EnrollmentFormProps2023> = ({
  enrollData,
  setEnrollData,
  previousPage,
  nextPage,
  plan,
  inReview,
}) => {
  const t = useTranslate();

  const handleHowPay = (e: SyntheticEvent<HTMLInputElement>): void => {
    const value = e.currentTarget.value;

    const premiumDirectPay = value === HowPay.DIRECT_PAY;
    const premiumDeducted = !premiumDirectPay;

    let newState = { ...enrollData, premiumDeducted, premiumDirectPay };
    if (premiumDirectPay) {
      newState = {
        ...newState,
        premiumWithhold: GetBenefitsFrom.WITHHOLD_NONE,
      };
    }
    setEnrollData(newState);
  };

  const handleBenefitsFrom = (e: SyntheticEvent<HTMLInputElement>): void => {
    const premiumWithhold = e.currentTarget.value;
    setEnrollData({ ...enrollData, premiumWithhold });
  };

  return (
    <div className="Paying">
      <FocusableHeader
        className="EnrollmentFormPage__header"
        text={t("enrollment_form.2021.paying_for_your_plan")}
      />

      <div className="ds-u-measure--wide">
        <PlanInfo plan={plan} />
        <span className="ds-u-visibility--screen-reader">
          {t("sr_only_additional_questions_may_appear")}
        </span>
        <section className="Paying__section">
          <FormattedMessage
            id="enroll_form.to_keep_your_plan_coverage"
            tagName="p"
            values={{
              drawer: <WhatsExtraHelpDrawer />,
            }}
          />

          <h2 className="ds-text-heading--xl">
            {t("enroll_form.ways_to_pay_your_plan_premium")}
          </h2>
          <FormattedMessage
            id="enroll_form.most_people_pay_by"
            tagName="p"
            values={{
              drawer: <MoreAboutPaymentsDrawer />,
            }}
          />

          <ChoiceList
            type="radio"
            choices={[
              {
                label: t("enroll_form.deduct_from_social_or_rail"),
                value: HowPay.DEDUCT,
                checked: enrollData.premiumDeducted,
              },
              {
                label: t("enroll_form.bill_me"),
                value: HowPay.DIRECT_PAY,
                checked: enrollData.premiumDirectPay,
              },
            ]}
            label={
              <h3 className="ds-text-heading--md mct-u-text-transform--none">
                {t("enroll_form.how_do_you_want_to_pay_optional")}
              </h3>
            }
            aria-label={t("enroll_form.how_do_you_want_to_pay_optional")}
            name="how_pay"
            onChange={handleHowPay}
          />

          {enrollData.premiumDeducted && (
            <>
              <ChoiceList
                type="radio"
                choices={[
                  {
                    label: t("social_security"),
                    value: GetBenefitsFrom.SSI,
                    checked: enrollData.premiumWithhold === GetBenefitsFrom.SSI,
                  },
                  {
                    label: t("railroad_retirement_board"),
                    value: GetBenefitsFrom.RRB,
                    checked: enrollData.premiumWithhold === GetBenefitsFrom.RRB,
                  },
                ]}
                label={
                  <h3 className="ds-text-heading--md mct-u-text-transform--none">
                    {t("enroll_form.i_get_benefits_each_month_from")}
                  </h3>
                }
                aria-label={`${t("enroll_form.i_get_benefits_from")} ${t(
                  "enroll_form.optional"
                )}`}
                name="benefits_from"
                onChange={handleBenefitsFrom}
              />
            </>
          )}
        </section>
        <section>
          <h3>{t("enroll_form.premiums.more_about_paying")}</h3>
          <p>
            <strong>{t("enroll_form.premiums.other_help.leading_text")}</strong>
            , {t("enroll_form.premiums.other_help.info")}
            {` `}
            <MoreAboutPremiumsOtherHelpDrawer />
          </p>
          <p>
            <span className="ds-u-font-weight--semibold">
              {t("enroll_form.premiums.income_is_over.leading_text")}
            </span>
            , {t("enroll_form.premiums.income_is_over.info")}
            {` `}
            <MoreAboutIrmaaDrawer />
          </p>
        </section>
        <BackNextButtons
          inReview={inReview}
          nextPage={nextPage}
          previousPage={previousPage}
        />
      </div>
    </div>
  );
};

export default Paying;
